import { supabase } from '../supabase';
import {
  Organization,
  OrganizationMember,
  Invitation,
  MemberRole,
  OrgSettings,
  SubscriptionStatus,
  DEFAULT_ORG_SETTINGS,
  createDefaultSubscription,
  generateUniqueSlug,
  createInvitationExpiry,
  canAddMember,
  canCreateOrganization,
  ROLE_PERMISSIONS,
} from '../types/tenant';
import { PLAN_CONFIGS, TRIAL_DAYS } from '@/config/plans';

// =============================================================================
// ORGANIZATION CRUD
// =============================================================================

/**
 * Get all organizations owned by a user.
 */
export async function getUserOwnedOrganizations(userId: string): Promise<Organization[]> {
  const { data: orgs, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('owner_id', userId);

  if (error || !orgs) return [];

  return orgs.map((org) => ({
    id: org.id,
    name: org.name,
    slug: org.slug,
    ownerId: org.owner_id,
    createdAt: org.created_at,
    updatedAt: org.updated_at,
    stripeCustomerId: org.stripe_customer_id,
    subscription: org.subscription as SubscriptionStatus,
    settings: org.settings as OrgSettings,
  }));
}

/**
 * Create a new organization and add the creator as owner.
 */
export async function createOrganization(
  userId: string,
  data: {
    name: string;
    slug?: string;
    settings?: Partial<OrgSettings>;
  }
): Promise<Organization> {
  const ownedOrgs = await getUserOwnedOrganizations(userId);
  if (!canCreateOrganization(ownedOrgs)) {
    throw new Error(
      'Trial users can only create 1 organization. Please upgrade to a paid plan to create more.'
    );
  }

  const slug = data.slug || generateUniqueSlug(data.name);

  const existingOrg = await getOrganizationBySlug(slug);
  if (existingOrg) {
    throw new Error('Organization slug already exists');
  }

  const trialEnd = new Date(Date.now() + TRIAL_DAYS * 24 * 60 * 60 * 1000);

  // Create organization
  const { data: org, error: orgError } = await supabase
    .from('organizations')
    .insert({
      name: data.name,
      slug,
      owner_id: userId,
      subscription: {
        plan: 'starter',
        status: 'trialing',
        trialEndsAt: trialEnd.toISOString(),
        features: PLAN_CONFIGS.starter.features,
      },
      settings: { ...DEFAULT_ORG_SETTINGS, ...data.settings },
    })
    .select()
    .single();

  if (orgError || !org) {
    throw new Error('Failed to create organization: ' + orgError?.message);
  }

  const { data: profile } = await supabase
    .from('profiles')
    .select('organizations, default_org_id, email, display_name, first_name, last_name')
    .eq('id', userId)
    .single();

  const { data: authUser } = await supabase.auth.getUser();
  const userEmail = profile?.email || authUser?.user?.email || '';
  const displayName =
    profile?.display_name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
    authUser?.user?.user_metadata?.full_name ||
    '';

  const { error: memberError } = await supabase.from('organization_members').insert({
    organization_id: org.id,
    user_id: userId,
    email: userEmail,
    display_name: displayName,
    role: 'owner',
  });

  if (memberError) {
    await supabase.from('organizations').delete().eq('id', org.id);
    throw new Error('Failed to add owner as member: ' + memberError.message);
  }

  const currentOrgs: string[] = profile?.organizations || [];
  await supabase
    .from('profiles')
    .update({
      organizations: [...currentOrgs, org.id],
      default_org_id: profile?.default_org_id || org.id,
    })
    .eq('id', userId);

  // Convert to Organization type
  return {
    id: org.id,
    name: org.name,
    slug: org.slug,
    ownerId: org.owner_id,
    createdAt: org.created_at,
    updatedAt: org.updated_at,
    stripeCustomerId: org.stripe_customer_id,
    subscription: org.subscription as SubscriptionStatus,
    settings: org.settings as OrgSettings,
  };
}

/**
 * Get organization by ID.
 */
export async function getOrganization(orgId: string): Promise<Organization | null> {
  const { data: org, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', orgId)
    .single();

  if (error || !org) return null;

  return {
    id: org.id,
    name: org.name,
    slug: org.slug,
    ownerId: org.owner_id,
    createdAt: org.created_at,
    updatedAt: org.updated_at,
    stripeCustomerId: org.stripe_customer_id,
    subscription: org.subscription as SubscriptionStatus,
    settings: org.settings as OrgSettings,
  };
}

/**
 * Get organization by slug.
 */
export async function getOrganizationBySlug(slug: string): Promise<Organization | null> {
  const { data: org, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('slug', slug)
    .single();

  if (error || !org) return null;

  return {
    id: org.id,
    name: org.name,
    slug: org.slug,
    ownerId: org.owner_id,
    createdAt: org.created_at,
    updatedAt: org.updated_at,
    stripeCustomerId: org.stripe_customer_id,
    subscription: org.subscription as SubscriptionStatus,
    settings: org.settings as OrgSettings,
  };
}

/**
 * Update organization details.
 */
export async function updateOrganization(
  orgId: string,
  data: Partial<Pick<Organization, 'name' | 'settings'>>
): Promise<void> {
  const updateData: any = {};
  if (data.name) updateData.name = data.name;
  if (data.settings) updateData.settings = data.settings;

  const { error } = await supabase.from('organizations').update(updateData).eq('id', orgId);

  if (error) {
    throw new Error('Failed to update organization: ' + error.message);
  }
}

/**
 * Delete an organization and all its data.
 * Only the owner can delete an organization.
 */
export async function deleteOrganization(orgId: string, userId: string): Promise<void> {
  const org = await getOrganization(orgId);
  if (!org) {
    throw new Error('Organization not found');
  }

  if (org.ownerId !== userId) {
    throw new Error('Only the owner can delete the organization');
  }

  // Get all members before deletion (for cleanup)
  const { data: members } = await supabase
    .from('organization_members')
    .select('user_id')
    .eq('organization_id', orgId);

  // Delete organization (cascade will handle members and projects)
  const { error } = await supabase.from('organizations').delete().eq('id', orgId);

  if (error) {
    throw new Error('Failed to delete organization: ' + error.message);
  }

  // Update all members' profiles to remove this org
  if (members) {
    for (const member of members) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organizations, default_org_id')
        .eq('id', member.user_id)
        .single();

      if (profile) {
        const orgs: string[] = (profile.organizations || []).filter((id: string) => id !== orgId);
        await supabase
          .from('profiles')
          .update({
            organizations: orgs,
            default_org_id:
              profile.default_org_id === orgId ? orgs[0] || null : profile.default_org_id,
          })
          .eq('id', member.user_id);
      }
    }
  }
}

// =============================================================================
// USER ORGANIZATIONS
// =============================================================================

/**
 * Get all organizations a user belongs to.
 */
export async function getUserOrganizations(userId: string): Promise<Organization[]> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('organizations')
    .eq('id', userId)
    .single();

  if (!profile || !profile.organizations || profile.organizations.length === 0) {
    return [];
  }

  // Fetch all organizations
  const { data: orgs, error } = await supabase
    .from('organizations')
    .select('*')
    .in('id', profile.organizations);

  if (error || !orgs) return [];

  return orgs.map((org) => ({
    id: org.id,
    name: org.name,
    slug: org.slug,
    ownerId: org.owner_id,
    createdAt: org.created_at,
    updatedAt: org.updated_at,
    stripeCustomerId: org.stripe_customer_id,
    subscription: org.subscription as SubscriptionStatus,
    settings: org.settings as OrgSettings,
  }));
}

/**
 * Get the user's current/default organization.
 */
export async function getUserDefaultOrganization(userId: string): Promise<Organization | null> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('default_org_id, organizations')
    .eq('id', userId)
    .single();

  if (!profile) return null;

  const defaultOrgId = profile.default_org_id;

  if (!defaultOrgId) {
    // Return first org if no default
    const orgs = await getUserOrganizations(userId);
    return orgs[0] || null;
  }

  return getOrganization(defaultOrgId);
}

/**
 * Switch user's active/default organization.
 */
export async function switchOrganization(userId: string, orgId: string): Promise<void> {
  // Verify user is a member
  const { data: member } = await supabase
    .from('organization_members')
    .select('id')
    .eq('organization_id', orgId)
    .eq('user_id', userId)
    .single();

  if (!member) {
    throw new Error('User is not a member of this organization');
  }

  await supabase.from('profiles').update({ default_org_id: orgId }).eq('id', userId);
}

// =============================================================================
// MEMBER MANAGEMENT
// =============================================================================

/**
 * Get all members of an organization.
 */
export async function getOrganizationMembers(orgId: string): Promise<OrganizationMember[]> {
  const { data: members, error } = await supabase
    .from('organization_members')
    .select('*')
    .eq('organization_id', orgId)
    .order('joined_at', { ascending: true });

  if (error || !members) return [];

  return members.map((member) => ({
    id: member.user_id,
    email: member.email,
    displayName: member.display_name,
    photoUrl: member.photo_url,
    role: member.role as MemberRole,
    joinedAt: member.joined_at,
    invitedBy: member.invited_by,
  }));
}

/**
 * Get a specific member's data.
 */
export async function getOrganizationMember(
  orgId: string,
  userId: string
): Promise<OrganizationMember | null> {
  const { data: member, error } = await supabase
    .from('organization_members')
    .select('*')
    .eq('organization_id', orgId)
    .eq('user_id', userId)
    .single();

  if (error || !member) return null;

  return {
    id: member.user_id,
    email: member.email,
    displayName: member.display_name,
    photoUrl: member.photo_url,
    role: member.role as MemberRole,
    joinedAt: member.joined_at,
    invitedBy: member.invited_by,
  };
}

/**
 * Get user's role in an organization.
 */
export async function getUserRole(orgId: string, userId: string): Promise<MemberRole | null> {
  const member = await getOrganizationMember(orgId, userId);
  return member?.role || null;
}

/**
 * Add a member to an organization (after invitation accepted).
 */
export async function addMember(
  orgId: string,
  userId: string,
  email: string,
  role: MemberRole,
  invitedBy?: string
): Promise<OrganizationMember> {
  const org = await getOrganization(orgId);
  if (!org) {
    throw new Error('Organization not found');
  }

  // Check member limit
  const members = await getOrganizationMembers(orgId);
  if (!canAddMember(org.subscription, members.length)) {
    throw new Error('Organization has reached the maximum number of members for its plan');
  }

  // Add member
  const { data: member, error: memberError } = await supabase
    .from('organization_members')
    .insert({
      organization_id: orgId,
      user_id: userId,
      email,
      role,
      invited_by: invitedBy,
    })
    .select()
    .single();

  if (memberError || !member) {
    throw new Error('Failed to add member: ' + memberError?.message);
  }

  // Update user's profile
  const { data: profile } = await supabase
    .from('profiles')
    .select('organizations, default_org_id')
    .eq('id', userId)
    .single();

  const currentOrgs: string[] = profile?.organizations || [];
  if (!currentOrgs.includes(orgId)) {
    await supabase
      .from('profiles')
      .update({
        organizations: [...currentOrgs, orgId],
        default_org_id: profile?.default_org_id || orgId,
      })
      .eq('id', userId);
  }

  return {
    id: member.user_id,
    email: member.email,
    displayName: member.display_name,
    photoUrl: member.photo_url,
    role: member.role as MemberRole,
    joinedAt: member.joined_at,
    invitedBy: member.invited_by,
  };
}

/**
 * Update a member's role.
 */
export async function updateMemberRole(
  orgId: string,
  memberId: string,
  newRole: MemberRole,
  requesterId: string
): Promise<void> {
  // Can't change owner's role
  const org = await getOrganization(orgId);
  if (org?.ownerId === memberId && newRole !== 'owner') {
    throw new Error("Cannot change the owner's role");
  }

  // Only admin/owner can change roles
  const requesterRole = await getUserRole(orgId, requesterId);
  if (!requesterRole || !ROLE_PERMISSIONS[requesterRole].canManageMembers) {
    throw new Error("You don't have permission to manage members");
  }

  const { error } = await supabase
    .from('organization_members')
    .update({ role: newRole })
    .eq('organization_id', orgId)
    .eq('user_id', memberId);

  if (error) {
    throw new Error('Failed to update member role: ' + error.message);
  }
}

/**
 * Remove a member from an organization.
 */
export async function removeMember(
  orgId: string,
  memberId: string,
  requesterId: string
): Promise<void> {
  const org = await getOrganization(orgId);
  if (!org) {
    throw new Error('Organization not found');
  }

  // Can't remove the owner
  if (org.ownerId === memberId) {
    throw new Error('Cannot remove the owner from the organization');
  }

  // Only admin/owner can remove (or user removing themselves)
  if (memberId !== requesterId) {
    const requesterRole = await getUserRole(orgId, requesterId);
    if (!requesterRole || !ROLE_PERMISSIONS[requesterRole].canManageMembers) {
      throw new Error("You don't have permission to remove members");
    }
  }

  // Remove member
  const { error } = await supabase
    .from('organization_members')
    .delete()
    .eq('organization_id', orgId)
    .eq('user_id', memberId);

  if (error) {
    throw new Error('Failed to remove member: ' + error.message);
  }

  // Update user's profile
  const { data: profile } = await supabase
    .from('profiles')
    .select('organizations, default_org_id')
    .eq('id', memberId)
    .single();

  if (profile) {
    const orgs: string[] = (profile.organizations || []).filter((id: string) => id !== orgId);
    await supabase
      .from('profiles')
      .update({
        organizations: orgs,
        default_org_id: profile.default_org_id === orgId ? orgs[0] || null : profile.default_org_id,
      })
      .eq('id', memberId);
  }
}

// =============================================================================
// INVITATIONS
// =============================================================================

/**
 * Generate a unique invitation token.
 */
function generateInvitationToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

/**
 * Create an invitation to join an organization.
 */
export async function createInvitation(
  orgId: string,
  email: string,
  role: MemberRole,
  invitedBy: string,
  invitedByName?: string
): Promise<Invitation> {
  const org = await getOrganization(orgId);
  if (!org) {
    throw new Error('Organization not found');
  }

  // Check for existing pending invitation
  const existingInvite = await getInvitationByEmail(orgId, email);
  if (existingInvite && existingInvite.status === 'pending') {
    throw new Error('An invitation has already been sent to this email');
  }

  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 7);

  const { data: invitation, error } = await supabase
    .from('invitations')
    .insert({
      organization_id: orgId,
      org_name: org.name,
      email: email.toLowerCase(),
      role,
      token: generateInvitationToken(),
      status: 'pending',
      invited_by: invitedBy,
      invited_by_name: invitedByName,
      expires_at: expiresAt.toISOString(),
    })
    .select()
    .single();

  if (error || !invitation) {
    throw new Error('Failed to create invitation: ' + error?.message);
  }

  return {
    id: invitation.id,
    orgId: invitation.organization_id,
    orgName: invitation.org_name,
    email: invitation.email,
    role: invitation.role as MemberRole,
    token: invitation.token,
    status: invitation.status as any,
    invitedBy: invitation.invited_by,
    invitedByName: invitation.invited_by_name,
    createdAt: invitation.created_at,
    expiresAt: invitation.expires_at,
    acceptedAt: invitation.accepted_at ?? undefined,
  };
}

/**
 * Get invitation by token (for accepting).
 */
export async function getInvitationByToken(token: string): Promise<Invitation | null> {
  const { data: invitation, error } = await supabase
    .from('invitations')
    .select('*')
    .eq('token', token)
    .single();

  if (error || !invitation) return null;

  return {
    id: invitation.id,
    orgId: invitation.organization_id,
    orgName: invitation.org_name,
    email: invitation.email,
    role: invitation.role as MemberRole,
    token: invitation.token,
    status: invitation.status as any,
    invitedBy: invitation.invited_by,
    invitedByName: invitation.invited_by_name,
    createdAt: invitation.created_at,
    expiresAt: invitation.expires_at,
    acceptedAt: invitation.accepted_at ?? undefined,
  };
}

/**
 * Get pending invitation for an email in an organization.
 */
export async function getInvitationByEmail(
  orgId: string,
  email: string
): Promise<Invitation | null> {
  const { data: invitation, error } = await supabase
    .from('invitations')
    .select('*')
    .eq('organization_id', orgId)
    .eq('email', email.toLowerCase())
    .eq('status', 'pending')
    .single();

  if (error || !invitation) return null;

  // Check if expired
  const expiresAt = new Date(invitation.expires_at);
  if (expiresAt < new Date()) return null;

  return {
    id: invitation.id,
    orgId: invitation.organization_id,
    orgName: invitation.org_name,
    email: invitation.email,
    role: invitation.role as MemberRole,
    token: invitation.token,
    status: invitation.status as any,
    invitedBy: invitation.invited_by,
    invitedByName: invitation.invited_by_name,
    createdAt: invitation.created_at,
    expiresAt: invitation.expires_at,
    acceptedAt: invitation.accepted_at ? (invitation.accepted_at ?? undefined) : undefined,
  };
}

/**
 * Get all pending invitations for an organization.
 */
export async function getOrganizationInvitations(orgId: string): Promise<Invitation[]> {
  const { data: invitations, error } = await supabase
    .from('invitations')
    .select('*')
    .eq('organization_id', orgId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error || !invitations) return [];

  return invitations
    .filter((inv) => new Date(inv.expires_at) > new Date())
    .map((invitation) => ({
      id: invitation.id,
      orgId: invitation.organization_id,
      orgName: invitation.org_name,
      email: invitation.email,
      role: invitation.role as MemberRole,
      token: invitation.token,
      status: invitation.status as any,
      invitedBy: invitation.invited_by,
      invitedByName: invitation.invited_by_name,
      createdAt: invitation.created_at,
      expiresAt: invitation.expires_at,
      acceptedAt: invitation.accepted_at ?? undefined,
    }));
}

/**
 * Get all pending invitations for a user (by email).
 */
export async function getUserPendingInvitations(email: string): Promise<Invitation[]> {
  const { data: invitations, error } = await supabase
    .from('invitations')
    .select('*')
    .eq('email', email.toLowerCase())
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error || !invitations) return [];

  // Filter out expired ones
  const now = new Date();
  return invitations
    .filter((inv) => new Date(inv.expires_at) > now)
    .map((invitation) => ({
      id: invitation.id,
      orgId: invitation.organization_id,
      orgName: invitation.org_name,
      email: invitation.email,
      role: invitation.role as MemberRole,
      token: invitation.token,
      status: invitation.status as any,
      invitedBy: invitation.invited_by,
      invitedByName: invitation.invited_by_name,
      createdAt: invitation.created_at,
      expiresAt: invitation.expires_at,
      acceptedAt: invitation.accepted_at ? (invitation.accepted_at ?? undefined) : undefined,
    }));
}

/**
 * Accept an invitation and join the organization.
 */
export async function acceptInvitation(
  token: string,
  userId: string,
  userEmail: string
): Promise<{ orgId: string; role: MemberRole }> {
  const invitation = await getInvitationByToken(token);

  if (!invitation) {
    throw new Error('Invitation not found');
  }

  if (invitation.status !== 'pending') {
    throw new Error('Invitation is no longer valid');
  }

  if (new Date(invitation.expiresAt) < new Date()) {
    // Mark as expired
    await supabase.from('invitations').update({ status: 'expired' }).eq('id', invitation.id);
    throw new Error('Invitation has expired');
  }

  // Verify email matches (case-insensitive)
  if (invitation.email.toLowerCase() !== userEmail.toLowerCase()) {
    throw new Error('Invitation email does not match your account');
  }

  // Check if already a member
  const existingMember = await getOrganizationMember(invitation.orgId, userId);
  if (existingMember) {
    // Mark invitation as accepted anyway
    await supabase
      .from('invitations')
      .update({
        status: 'accepted',
        accepted_at: new Date().toISOString(),
      })
      .eq('id', invitation.id);
    return { orgId: invitation.orgId, role: existingMember.role };
  }

  // Add as member
  await addMember(invitation.orgId, userId, userEmail, invitation.role, invitation.invitedBy);

  // Mark invitation as accepted
  await supabase
    .from('invitations')
    .update({
      status: 'accepted',
      accepted_at: new Date().toISOString(),
    })
    .eq('id', invitation.id);

  return { orgId: invitation.orgId, role: invitation.role };
}

/**
 * Revoke a pending invitation.
 */
export async function revokeInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase
    .from('invitations')
    .update({ status: 'revoked' })
    .eq('id', invitationId);

  if (error) {
    throw new Error('Failed to revoke invitation: ' + error.message);
  }
}

/**
 * Resend an invitation (creates a new token and expiry).
 */
export async function resendInvitation(invitationId: string): Promise<void> {
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 7);

  const { error } = await supabase
    .from('invitations')
    .update({
      token: generateInvitationToken(),
      expires_at: expiresAt.toISOString(),
      status: 'pending',
    })
    .eq('id', invitationId);

  if (error) {
    throw new Error('Failed to resend invitation: ' + error.message);
  }
}

// =============================================================================
// SUBSCRIPTION HELPERS
// =============================================================================

/**
 * Update organization's subscription (called by Stripe webhook).
 */
export async function updateSubscription(
  orgId: string,
  subscription: Partial<SubscriptionStatus>
): Promise<void> {
  const { error } = await supabase.from('organizations').update({ subscription }).eq('id', orgId);

  if (error) {
    throw new Error('Failed to update subscription: ' + error.message);
  }
}

/**
 * Update Stripe customer ID for organization.
 */
export async function setStripeCustomerId(orgId: string, stripeCustomerId: string): Promise<void> {
  const { error } = await supabase
    .from('organizations')
    .update({ stripe_customer_id: stripeCustomerId })
    .eq('id', orgId);

  if (error) {
    throw new Error('Failed to update Stripe customer ID: ' + error.message);
  }
}

/**
 * Get organization by Stripe customer ID.
 */
export async function getOrganizationByStripeCustomerId(
  stripeCustomerId: string
): Promise<Organization | null> {
  const { data: org, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('stripe_customer_id', stripeCustomerId)
    .single();

  if (error || !org) return null;

  return {
    id: org.id,
    name: org.name,
    slug: org.slug,
    ownerId: org.owner_id,
    createdAt: org.created_at,
    updatedAt: org.updated_at,
    stripeCustomerId: org.stripe_customer_id,
    subscription: org.subscription as SubscriptionStatus,
    settings: org.settings as OrgSettings,
  };
}
